import { Route, Switch, useHistory } from "react-router-dom"
import React, { useState, useMemo, useCallback, useEffect } from "react"
import { makeStyles, Button, Paper, Box } from "@material-ui/core"
import ApiCustomer from "services/api-customer"
import useCheckPermission from "utils/auth/useCheckPermission"
import NotAllowedNotice from "components/NotAllowedNotice"
import PERMISSION_CODES from "constants/permission-codes"
import { useWrapHandleInvalidToken } from "utils/invalid-token"
// import useAttachmentPopOver from "components/request-for-quotation/useAttachmentPopOver"
import useDatatablesFetchHelper from "utils/datatables/useDatatablesFetchHelper"
import { formatDate } from "utils/form-data"
import ApiReceiptOfInvoice from "services/api-receipt-of-invoice"
import { ROUTE_RECEIPT_OF_INVOICE } from "constants/routeConstants"
import BaseTableNew from "components/common/BaseTableNew"
import BasePage from "components/common/BasePage"
import FilterDatatable from "components/common/FilterDatatable"
import RouteBreadcrumb from "components/page-layout/breadcrumbs/RouteBreadcrumb"
import ReceiptOfInvoiceCreatePage from "./ReceiptOfInvoiceCreatePage"
import ReceiptOfInvoiceItemPage from "./ReceiptOfInvoiceItemPage"

const useStyles = makeStyles((theme) => ({
  filterHeaderTitle: {
    color: theme.palette.sectionTitle.main,
  },
  createButton: {
    margin: "16px",
    marginRight: 0,
    marginLeft: "auto",
    display: "block",
  },
  pageTitle: {
    color: theme.palette.sectionTitle.main,
    marginBottom: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  materialAutocomplete: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 200,
  },
  filterBox: {
    border: `1px solid #bccce3`,
    padding: theme.spacing(1),
    borderRadius: 5,
    marginBottom: theme.spacing(3),
  },
  filterPickerContainer: {
    display: "flex",
    marginBottom: theme.spacing(-1),
  },
  filterChips: {
    marginTop: theme.spacing(1),
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  materialChip: {
    backgroundColor: "#673ab7",
    color: "white",
    "& .MuiChip-deleteIcon": {
      color: "#ffffffa8",
    },
  },
  // Remove table shadow manually
  table: {
    "& .MuiPaper-root": {
      boxShadow: "none !important",
    },
    "& tr > th": {
      borderBottom: "1px solid black",
      lineHeight: 1,
    },
    // Remove bottom border in footer
    "& .MuiTableFooter-root tr > *": {
      borderBottom: "none",
    },
  },
  filterMaterialLoading: {
    position: "absolute",
    right: 16,
  },
  launchIcon: {
    position: "relative",
    top: 3,
    left: 2,
    fontSize: theme.typography.body1.fontSize,
  },
  linkContainer: {
    position: "relative",
  },
  linkLoading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}))

const fetchCustomerData = async (searchText) =>
  (
    await ApiCustomer.get({
      searchText,
      limit: 10,
      fromColumns: ["name", "initial"],
    })
  ).data

export default function ReceiptOfInvoiceListPage() {
  const history = useHistory()
  const classes = useStyles()

  const { checkWithoutUpdateState } = useCheckPermission()
  const [params, setParams] = useState({})

  const canCreateItem = useMemo(
    () =>
      checkWithoutUpdateState({
        permissions: [PERMISSION_CODES.READ_RECEIPT_OF_INVOICE],
      }),
    [checkWithoutUpdateState]
  )

  const wrappedFetchItems = useWrapHandleInvalidToken(
    ApiReceiptOfInvoice.get,
    () => []
  )

  const getParams = useCallback(() => {
    const paramsCopy = {}
    return { ...paramsCopy, ...params }
  }, [params])
  const { options: helperOptions, data, loading } = useDatatablesFetchHelper({
    fetchData: wrappedFetchItems,
    getParams,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnSettings = useMemo(() => {
    const renderCheck = (dataIndex, field) => {
      const item = data[dataIndex]
      return item?.[field] ? "√" : ""
    }

    return [
      {
        name: "rowNumber",
        label: "No",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRenderLite: (dataIndex) => dataIndex + 1,
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "invoiceNumber",
        label: "Nomor Invoice",
      },
      {
        name: "invoiceDate",
        label: "Tanggal Invoice",
        options: {
          searchable: false,
          customBodyRender: (val) => formatDate(val), // pakai helper yg sama kaya contoh mentor
        },
      },
      {
        name: "currency",
        label: "Curr",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (val) => val || "IDR",
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "totalAmount",
        label: "Total Amount",
        options: {
          searchable: false,
          customBodyRender: (val) =>
            val || val === 0 ? val.toLocaleString("id-ID") : "-",
          setCellProps: () => ({ style: { textAlign: "right" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
        },
      },

      // ====== Kelengkapan Dokumen ======
      {
        name: "fpOriginal",
        label: "FP Asli",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "fpOriginal"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "fpCopy",
        label: "FP Copy",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "fpCopy"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "invoiceOriginal",
        label: "Invoice Asli",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) =>
            renderCheck(index, "invoiceOriginal"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "invoiceCopy",
        label: "Invoice Copy",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "invoiceCopy"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "grnOriginal",
        label: "GRN Asli",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "grnOriginal"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "doOriginal",
        label: "DO Asli",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "doOriginal"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
      {
        name: "poCopy",
        label: "PO Copy",
        options: {
          searchable: false,
          filter: false,
          sort: false,
          customBodyRenderLite: (index) => renderCheck(index, "poCopy"),
          setCellProps: () => ({ style: { textAlign: "center" } }),
          setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        },
      },
    ]
  }, [data])

  const goToItemAtIndex = (_, { dataIndex }) => {
    const item = data[dataIndex]
    window.open(`${ROUTE_RECEIPT_OF_INVOICE}/${item.idReceiptOfInvoice}`)
  }

  const options = {
    ...helperOptions,
    sort: false,
    onRowClick: goToItemAtIndex,
    setTableProps: () => {
      return {
        size: "small",
      }
    },
    // setRowProps,
  }

  useEffect(() => {
    console.log({ params })
  }, [params])

  const handleFilterChange = (filterObj) => {
    const { search, ...rest } = filterObj

    const newParams = { ...rest }

    // Kalau ada input di kolom text/date, FilterDatatable taruh di "search" (array)
    if (Array.isArray(search) && search.length > 0) {
      // Gabungkan semua value jadi satu string (misal: "INV-001 2025-11-10 1000000")
      const searchText = search
        .map((s) => s?.value)
        .filter(Boolean)
        .join(" ")

      if (searchText) {
        newParams.searchText = searchText
      }
    }

    setParams(newParams)
  }

  const PAGE_PERMISSIONS = [PERMISSION_CODES.READ_RECEIPT_OF_INVOICE]

  const allowed = useMemo(
    () => checkWithoutUpdateState({ permissions: PAGE_PERMISSIONS }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkWithoutUpdateState]
  )

  if (!allowed) {
    return (
      <BasePage maxWidth={800}>
        <NotAllowedNotice />
      </BasePage>
    )
  }

  const initiateCreateItem = () => {
    history.push(`${ROUTE_RECEIPT_OF_INVOICE}/create`)
  }

  return (
    <Paper style={{ maxWidth: 1300 }}>
      <Box p={2}>
        <Switch>
          <Route exact path={ROUTE_RECEIPT_OF_INVOICE}>
            <FilterDatatable
              columns={[
                {
                  key: "invoiceNumber",
                  label: "Invoice Number",
                  type: "text",
                },
                {
                  key: "invoiceDate",
                  label: "Invoice Date",
                  type: "date",
                },
                {
                  key: "customerId",
                  label: "Customer",
                  type: "list",
                  fetchData: fetchCustomerData,
                  getOptionLabel: (opt) => `${opt.name} [${opt.initial}]`,
                  getOptionSelected: (opt, val) => opt.idCust === val?.idCust,
                  getOptionValue: (opt) => opt?.idCust,
                },
              ]}
              onChange={handleFilterChange}
            />
            <BaseTableNew
              data={data}
              columns={columnSettings}
              options={options}
              loading={loading}
              onClick={goToItemAtIndex}
            />
            {canCreateItem && (
              <Button
                className={classes.createButton}
                variant="contained"
                disableElevation
                color="primary"
                onClick={initiateCreateItem}
              >
                Create New Receipt Of Invoice
              </Button>
            )}
          </Route>
          <RouteBreadcrumb
            path={`${ROUTE_RECEIPT_OF_INVOICE}/create`}
            component={ReceiptOfInvoiceCreatePage}
            name="Create Receipt Of Invoice"
          />
          <RouteBreadcrumb
            name="Receipt Of Invoice Detail"
            path={`${ROUTE_RECEIPT_OF_INVOICE}/:id`}
            component={ReceiptOfInvoiceItemPage}
          />
        </Switch>
      </Box>
    </Paper>
  )
}
